import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import { TeamTemplateParty } from "work/team-templates/team-template-party";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";
import { z } from "zod";

export type TeamTemplateJSON = z.infer<typeof TeamTemplateSchema.TeamTemplateJSONZ>;
export type TeamTemplateParams = z.infer<typeof TeamTemplateSchema.TeamTemplateZ>;

export default class TeamTemplateSchema {
  public static readonly TeamTemplateJSONZ = z.object({
    id: z.uuid().nullish(),
    created: z.iso.datetime({ offset: true }).nullish(),
    archived: z.iso.datetime({ offset: true }).nullish(),
    isMasked: z.boolean().nullish(),
    name: z.string(),
    description: z.string().nullish(),
    leader: EntityMemberSchema.EntityMemberJSONZ,
    members: z.array(EntityMemberSchema.EntityMemberJSONZ).nullish(),
    marketplaceId: z.uuid().nullish(),
    // Nullish because a stored payload minted before the column existed has no party, and because the
    // barebones response does not carry one. It is read to describe a template, never to decide which
    // panel may list it — the query already did that.
    party: z.enum(TeamTemplateParty).nullish(),
    avatarId: z.uuid().nullish()
  });
  public static readonly TeamTemplateZ = z.object({
    id: z.instanceof(Guid).nullish(),
    created: z.instanceof(AHDate).nullish(),
    archived: z.instanceof(AHDate).nullish(),
    isMasked: z.boolean().default(false).nullish(),
    name: z.instanceof(VendorTeamName),
    description: z.string().nullish(),
    leader: z.instanceof(EntityMember),
    members: z.array(z.instanceof(EntityMember)).default([]),
    marketplaceId: z.instanceof(Guid).nullish(),
    party: z.enum(TeamTemplateParty).nullish(),
    avatarId: z.instanceof(Guid).nullish()
  });

  public static validate(values: unknown): TeamTemplateParams {
    return parseOrThrow(TeamTemplateSchema.TeamTemplateZ, values, {
      logError: true,
      schemaName: "TeamTemplateSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamTemplateJSON {
    return parseOrThrow(TeamTemplateSchema.TeamTemplateJSONZ, values, {
      logError: true,
      schemaName: "TeamTemplateSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamTemplateParams {
    try {
      TeamTemplateSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is TeamTemplateJSON {
    try {
      TeamTemplateSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: TeamTemplateJSON): TeamTemplateParams {
    const parsed = parseOrThrow(TeamTemplateSchema.TeamTemplateJSONZ, data, {
      logError: true,
      schemaName: "TeamTemplateSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return TeamTemplateSchema.codec.decode(parsed);
  }

  public static serialize(document: TeamTemplateParams): TeamTemplateJSON {
    const parsed = TeamTemplateSchema.validate(document);
    return TeamTemplateSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(TeamTemplateSchema.TeamTemplateJSONZ, TeamTemplateSchema.TeamTemplateZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id) ?? undefined,
      created: AHDate.fromJSON(data.created) ?? undefined,
      archived: AHDate.fromJSON(data.archived) ?? undefined,
      isMasked: data.isMasked ?? false,
      name: new VendorTeamName(data.name),
      description: data.description ?? undefined,
      leader: EntityMember.fromJSON(data.leader)!,
      members: data.members?.map((member) => EntityMember.fromJSON(member)!) ?? [],
      marketplaceId: Guid.fromJSON(data.marketplaceId) ?? undefined,
      party: data.party ?? undefined,
      avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
    }),
    encode: (teamTemplate) => ({
      id: teamTemplate.id?.toJSON(),
      created: teamTemplate.created?.toJSON(),
      archived: teamTemplate.archived?.toJSON(),
      isMasked: teamTemplate.isMasked ?? false,
      name: teamTemplate.name.toJSON(),
      description: teamTemplate.description,
      leader: teamTemplate.leader.toJSON(),
      members: teamTemplate.members?.map((member) => member.toJSON()),
      marketplaceId: teamTemplate.marketplaceId?.toJSON(),
      party: teamTemplate.party,
      avatarId: teamTemplate.avatarId?.toJSON(),
    }),
  });
}
